import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";

const LINKS = [
  { label: "Menus", href: "#menus" },
  { label: "Services", href: "#services" },
  { label: "Philosophy", href: "#philosophy" },
  { label: "The Chef", href: "#chef" },
  { label: "Gallery", href: "#gallery" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="main-navigation"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-noir/10 bg-bone/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3">
          <LogoMark size={32} />
          <Wordmark />
        </a>

        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="group relative text-[13px] font-medium uppercase tracking-[0.18em] text-ink-soft transition-colors duration-300 hover:text-noir"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-right scale-x-0 bg-gold transition-transform duration-500 ease-out group-hover:origin-left group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="mailto:kas@kaseybeaumont.com?subject=Catering%20Enquiry%20—%20kaseybeaumont.com"
            data-testid="nav-cta-button"
            className="group hidden items-center gap-2 rounded-full border border-noir/15 bg-noir px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.16em] text-bone transition-colors duration-300 hover:bg-truffle sm:inline-flex"
          >
            Request Consultation
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-noir/15 text-noir lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-noir/10 bg-bone/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="border-b border-noir/5 py-3 font-serif text-2xl text-noir"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="mailto:kas@kaseybeaumont.com?subject=Catering%20Enquiry%20—%20kaseybeaumont.com"
                data-testid="mobile-nav-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-noir px-5 py-3 text-[12px] font-medium uppercase tracking-[0.16em] text-bone"
              >
                Request Consultation <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
